import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { NameCircle } from '~/components/Global/Elements/NameCircle/NameCircle'
import { AsideActionWrapper } from '~/components/Layout/AsideActionWrapper/AsideActionWrapper'
import {
  applicantModalAsideActions,
  applicantModalAsideActionsTitle
} from '~/components/Scopes/Applicants/ApplicantModal/components/ApplicantModalActions.css'
import { ActionsButtonList } from '~/domains/actions/components/ActionsButtonList/ActionsButtonList'
import type { TargetActionName } from '~/domains/actions/components/ActionsButtonList/actionsForTarget'
import { useConfig } from '~/domains/configuration/queries/config'
import { contactAccessDeniedMessage } from '~/domains/contacts/accessDenied'
import { useContactPicture } from '~/domains/contacts/queries/contactPictures'
import { useContact } from '~/domains/contacts/queries/useContact'
import { useContactHead } from '~/domains/contacts/queries/useContactHead'
import type { ContactHeadResponse } from '~/domains/contacts/types'
import { ContactTasks } from '~/domains/tasks/components/ContactTasks/ContactTasks'
import type { AsideActionPosition } from '~/types/global'
import { formatName } from '~/utils/names'
import { ContextPhoneButton } from './ContextPhoneButton'
import {
  contextAvatar,
  contextBody,
  contextCompany,
  contextHeaderActions,
  contextName,
  contextNameLink,
  contextRole
} from './MessagingContext.css'
import { MessagingContextAccessDenied } from './MessagingContextAccessDenied'
import { MessagingContextPanel } from './MessagingContextPanel'

type MessagingContactHead = ContactHeadResponse & {
  company?: {
    id: number
    name: string
    companyStatus?: {
      name: string
      color?: string
      background_color?: string
    } | null
  } | null
}

const HIDDEN_ACTIONS: TargetActionName[] = ['message', 'email']
const ACTION_POSITIONS: AsideActionPosition[] = ['bottom']

export const ContactContext = ({
  id,
  onToggle,
  onActionOpenChange
}: {
  id: number
  onToggle?: () => void
  onActionOpenChange?: (isOpen: boolean) => void
}) => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data, isLoading } = useContactHead<MessagingContactHead>({
    contactId: id,
    additionalFields: ['role', 'customRole', 'company', 'phone', 'phoneMobile']
  })
  // Same as the applicant column: the filtered head is absent, the item endpoint names the followers
  const headMissing = !isLoading && !data
  const { failureReason: accessError } = useContact({
    id: headMissing ? id : undefined,
    showToast: false
  })
  const { data: picture } = useContactPicture({ contactId: id })
  const [currentAction, setCurrentAction] = useState<string | null>(null)
  const [currentHoverAction, setCurrentHoverAction] = useState<string | null>(
    null
  )

  useEffect(() => {
    onActionOpenChange?.(currentAction !== null)
  }, [currentAction, onActionOpenChange])

  if (isLoading) {
    return <MessagingContextPanel loading onToggle={onToggle} />
  }

  if (!data) {
    return (
      <MessagingContextAccessDenied
        description={contactAccessDeniedMessage(accessError)}
        onToggle={onToggle}
      />
    )
  }

  const roleLabel = data.customRole ?? data.role?.name
  const phone = data.phoneMobile || data.phone

  return (
    <MessagingContextPanel
      onToggle={onToggle}
      header={
        <>
          {picture?.profilePicUrl ? (
            <img
              src={picture.profilePicUrl}
              alt={formatName(data)}
              className={contextAvatar}
            />
          ) : (
            <NameCircle name={formatName(data)} size={56} />
          )}
          {data.company?.id ? (
            <a
              href={`${legacyUrl}/admin/entreprises/edit/${data.company.id}#contacts/${data.id}`}
              className={clsx(contextName, contextNameLink)}
            >
              {formatName(data)}
            </a>
          ) : (
            <div className={contextName}>{formatName(data)}</div>
          )}
          {roleLabel && <div className={contextRole}>{roleLabel}</div>}
          <div className={contextHeaderActions}>
            {data.company?.companyStatus?.name && (
              <Badge
                title={data.company.companyStatus.name}
                backgroundColor={
                  data.company.companyStatus.background_color ?? ''
                }
              />
            )}
            {phone && <ContextPhoneButton phone={phone} />}
          </div>
        </>
      }
    >
      {data.company?.name && (
        <div className={contextBody}>
          <div className={contextCompany}>{data.company.name}</div>
        </div>
      )}
      <div className={applicantModalAsideActions}>
        <div className={applicantModalAsideActionsTitle}>
          {currentHoverAction ?? currentAction ?? 'Actions rapides'}
        </div>
        <ActionsButtonList
          target={{ type: 'contact', ids: [id] }}
          hiddenActions={HIDDEN_ACTIONS}
          onActionHover={setCurrentHoverAction}
          onCurrentActionChange={setCurrentAction}
          availablePositions={ACTION_POSITIONS}
          defaultPosition="bottom"
        />
        <AsideActionWrapper position="bottom" />
      </div>
      <ContactTasks contactId={id} />
    </MessagingContextPanel>
  )
}
